import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

import "highlight.js/styles/github-dark.css";

type MarkdownMessageProps = {
  content: string;
  fontSize?: string;
};

/** Renderer de markdown reutilizável (títulos, listas, tabelas, negrito/itálico, links e código). */
const MarkdownMessage = ({
  content,
  fontSize = "0.9rem",
}: MarkdownMessageProps) => {
  return (
    <Box
      sx={{
        wordBreak: "break-word",
        fontSize,
        lineHeight: 1.7,
        "& > *:first-of-type": { mt: 0 },
        "& > *:last-child": { mb: 0 },
        "& p": { m: 0, mb: 1.5 },
        "& ul, & ol": { mt: 0.25, mb: 1.5, pl: 3 },
        "& li": { mb: 0.65 },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          mt: 1.75,
          mb: 1,
          fontWeight: 600,
          lineHeight: 1.35,
        },
        // Evita o tamanho “de página” do tema/browser dentro do bubble do chat.
        "& h1": { fontSize: "1.05rem" },
        "& h2": { fontSize: "1.02rem" },
        "& h3": { fontSize: "0.98rem" },
        "& h4, & h5, & h6": { fontSize: "0.95rem" },
        "& strong": { fontWeight: 600 },
        "& blockquote": {
          borderLeft: "3px solid",
          borderColor: "divider",
          pl: 1.5,
          ml: 0,
          mb: 1,
          color: "text.secondary",
        },
        "& pre": {
          borderRadius: 6,
          p: 1.5,
          overflowX: "auto",
          mb: 1,
          fontSize: "0.85rem",
        },
        "& code": {
          fontFamily: "Menlo, Consolas, monospace",
          fontSize: "0.85em",
        },
        "& :not(pre) > code": {
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
          borderRadius: 4,
          px: 0.6,
          py: 0.2,
        },
        "& table": {
          display: "block",
          overflowX: "auto",
          borderCollapse: "collapse",
          mb: 1,
        },
        "& th, & td": {
          border: "1px solid",
          borderColor: "divider",
          px: 1,
          py: 0.5,
          fontSize: "0.85rem",
          textAlign: "left",
        },
        "& th": { fontWeight: 600 },
        "& a": { color: "primary.main" },
        "& hr": { borderColor: "divider", my: 1.5 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownMessage;
